import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  X,
  MapPin,
  Calendar,
  User,
  Phone,
  Mail,
  KeyRound,
  Wrench,
  FileText,
  Clock,
  ChevronRight,
  ExternalLink,
  Loader2,
  Navigation,
  MessageSquare,
  Lock,
  Unlock,
  CalendarPlus,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  formatDate,
  getServiceCallListCustomerName,
  getServiceCallListCityState,
  formatCoolingType,
} from '@shared/utils';
import { serviceCallsAPI, clientsAPI } from '@shared/apiClient';
import { FlagIcon } from '@shared/flagIcons';
import Dock from '../../shell/Dock';
import { useShell } from '../../shell/ShellContext';
import { useDispatchWorkspace } from './DispatchContext';
import { useDragAssign } from '../../hooks/useDragAssign';
import { SOURCE_BADGE, technicianName } from './constants';
import { ServiceCallDetailsModal } from '../../pages/ServiceCalls';
import ScheduleRepairModal from './ScheduleRepairModal';

const STATUS_STYLES = {
  unassigned: 'bg-gray-100 text-gray-700',
  queued: 'bg-slate-100 text-slate-700',
  assigned: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-purple-100 text-purple-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-red-100 text-red-800',
  rescheduled: 'bg-orange-100 text-orange-800',
  return: 'bg-amber-100 text-amber-800',
};

function StatusPill({ status }) {
  if (!status) return null;
  const style = STATUS_STYLES[status] || 'bg-gray-100 text-gray-700';
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded text-2xs font-medium capitalize ${style}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

function Section({ icon: Icon, title, children, action }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <h4 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-gray-500">
          {Icon && <Icon className="h-3.5 w-3.5 text-gray-400" />}
          {title}
        </h4>
        {action}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-900 text-right break-words">{value}</span>
    </div>
  );
}

export default function DetailDock() {
  const queryClient = useQueryClient();
  const { selectedDetail, setSelectedDetail } = useDispatchWorkspace();
  const { openMessageThread } = useShell();
  const { unassignAssignment, unscheduleCall } = useDragAssign();
  const [openServiceCall, setOpenServiceCall] = useState(null);
  const [loadingCallId, setLoadingCallId] = useState(null);
  const [scheduleRepairOpen, setScheduleRepairOpen] = useState(false);

  const serviceCallId = selectedDetail?.type === 'assignment'
    ? selectedDetail.assignment?.id
    : selectedDetail?.type === 'serviceCall'
      ? selectedDetail.serviceCall?.id
      : selectedDetail?.item?.serviceCallId;

  const { data: callData } = useQuery(
    ['serviceCallDetail', serviceCallId],
    () => serviceCallsAPI.getServiceCall(serviceCallId),
    { enabled: !!serviceCallId }
  );

  const fullCall = callData?.data?.serviceCall;
  const item = selectedDetail?.item;
  const assignment = selectedDetail?.assignment;
  const serviceCall = selectedDetail?.serviceCall || fullCall;
  const point = selectedDetail?.point;

  const currentCall = fullCall || assignment || serviceCall || null;

  const scheduleLockMutation = useMutation(
    ({ id, scheduleLocked, scheduleLockedReason }) =>
      serviceCallsAPI.setScheduleLock(id, { scheduleLocked, scheduleLockedReason }),
    {
      onSuccess: (response) => {
        toast.success(response.data.message);
        queryClient.invalidateQueries(['serviceCallDetail', serviceCallId]);
        queryClient.invalidateQueries('calendarServiceCalls');
        queryClient.invalidateQueries('schedulingBacklog');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update schedule lock');
      },
    }
  );

  const handleToggleScheduleLock = () => {
    if (!currentCall?.id || !currentCall.scheduledDate) return;
    if (currentCall.scheduleLocked) {
      if (!window.confirm('Remove the schedule lock? Re-auto-schedule may move this visit again.')) return;
      scheduleLockMutation.mutate({ id: currentCall.id, scheduleLocked: false });
      return;
    }
    const reason = window.prompt(
      'Optional: why is this visit locked? (e.g. client requested this date)',
      currentCall.scheduleLockedReason || ''
    );
    if (reason === null) return;
    scheduleLockMutation.mutate({
      id: currentCall.id,
      scheduleLocked: true,
      scheduleLockedReason: reason.trim() || undefined,
    });
  };

  const clientId = fullCall?.clientId
    || fullCall?.client?.id
    || assignment?.client?.id
    || serviceCall?.client?.id
    || item?.clientId
    || null;

  const { data: clientResp, isLoading: clientLoading } = useQuery(
    ['detailClient', clientId],
    () => clientsAPI.getClient(clientId),
    { enabled: !!clientId }
  );

  const { data: historyResp, isLoading: historyLoading } = useQuery(
    ['detailClientHistory', clientId],
    () => serviceCallsAPI.getServiceCalls({ clientId, limit: 25 }),
    { enabled: !!clientId }
  );

  const openCall = async (id) => {
    if (!id || loadingCallId) return;
    setLoadingCallId(id);
    try {
      const response = await serviceCallsAPI.getServiceCall(id);
      setOpenServiceCall(response.data.serviceCall);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load service call');
    } finally {
      setLoadingCallId(null);
    }
  };

  if (!selectedDetail) return null;

  const client = clientResp?.data?.client
    || fullCall?.client
    || assignment?.client
    || serviceCall?.client
    || null;

  const history = (historyResp?.data?.serviceCalls || [])
    .slice()
    .sort((a, b) => {
      const da = new Date(a.completedDate || a.scheduledDate || a.createdAt);
      const db = new Date(b.completedDate || b.scheduledDate || b.createdAt);
      return db - da;
    });

  const title = item?.displayName
    || (assignment && getServiceCallListCustomerName(assignment))
    || (serviceCall && getServiceCallListCustomerName(serviceCall))
    || point?.label
    || 'Details';

  const badge = item ? SOURCE_BADGE[item.source] : null;

  const cityState = (currentCall && getServiceCallListCityState(currentCall))
    || item?.cityState
    || [client?.city, client?.state].filter(Boolean).join(', ');

  const fullAddress = [client?.address, client?.city, client?.state, client?.zipCode]
    .filter(Boolean)
    .join(', ');
  const mapsQuery = encodeURIComponent(fullAddress || cityState || title);

  const flags = client?.flags || [];

  return (
    <Dock
      id="detail"
      side="right"
      title="Details"
      defaultSize={340}
      minSize={280}
      maxSize={520}
      defaultPinned
      dataTourId="detail-dock"
    >
      <div className="flex flex-col h-full">
        {/* Header */}
        <div className="p-3 border-b border-surface-border">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 break-words">{title}</h3>
              <div className="flex flex-wrap items-center gap-1 mt-1">
                {badge && (
                  <span className={`inline-block px-1.5 py-0.5 rounded text-2xs font-medium ${badge.className}`}>
                    {badge.label}
                  </span>
                )}
                {currentCall?.status && <StatusPill status={currentCall.status} />}
                {client?.isCommercial && (
                  <span className="inline-block px-1.5 py-0.5 rounded text-2xs font-medium bg-indigo-100 text-indigo-800">
                    Commercial
                  </span>
                )}
                {client?.isContractClient && (
                  <span className="inline-block px-1.5 py-0.5 rounded text-2xs font-medium bg-slate-100 text-slate-700">
                    Contract
                  </span>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelectedDetail(null)}
              className="p-1 rounded hover:bg-surface-hover text-gray-400"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {cityState && (
            <div className="flex items-center gap-1.5 text-xs text-gray-600 mt-2">
              <MapPin className="h-3.5 w-3.5 text-gray-400" />
              <span className="truncate">{cityState}</span>
              {(fullAddress || cityState) && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${mapsQuery}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-auto inline-flex items-center gap-1 text-primary-600 hover:text-primary-800"
                  title="Open in maps"
                >
                  <Navigation className="h-3 w-3" />
                  Directions
                </a>
              )}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-3 space-y-4 text-sm">
          {/* Backlog helper hint */}
          {item && !currentCall && (
            <>
              {item.dueBucket && (
                <div className="text-xs">
                  <span className="text-gray-500">Due: </span>
                  <span className="font-medium capitalize">{item.dueBucket.replace('_', ' ')}</span>
                </div>
              )}
              <p className="text-xs text-gray-500">
                Drag to a technician column, calendar day, or tech dock to assign.
              </p>
            </>
          )}

          {/* Current call */}
          {currentCall && (
            <Section
              icon={Wrench}
              title="This service call"
              action={serviceCallId && (
                <button
                  type="button"
                  onClick={() => openCall(serviceCallId)}
                  className="inline-flex items-center gap-1 text-2xs font-medium text-primary-600 hover:text-primary-800"
                >
                  {loadingCallId === serviceCallId
                    ? <Loader2 className="h-3 w-3 animate-spin" />
                    : <ExternalLink className="h-3 w-3" />}
                  Open
                </button>
              )}
            >
              <div className="space-y-1.5 rounded-md bg-surface-secondary p-2">
                <InfoRow label="Type" value={currentCall.type?.name} />
                <InfoRow
                  label="Technician"
                  value={currentCall.technician ? technicianName(currentCall.technician) : 'Unassigned'}
                />
                {(currentCall.scheduledDate || currentCall.completedDate) && (
                  <InfoRow
                    label={currentCall.completedDate ? 'Completed' : 'Scheduled'}
                    value={formatDate(currentCall.completedDate || currentCall.scheduledDate)}
                  />
                )}
                {currentCall.description && (
                  <div className="pt-1 text-xs text-gray-700 whitespace-pre-wrap">
                    {currentCall.description}
                  </div>
                )}
                {currentCall.customerNotes && (
                  <div className="pt-1 text-xs">
                    <span className="font-medium text-gray-700">Customer notes: </span>
                    <span className="text-gray-700 whitespace-pre-wrap">{currentCall.customerNotes}</span>
                  </div>
                )}
                {currentCall.scheduleLocked && (
                  <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
                    <div className="inline-flex items-center gap-1 font-medium">
                      <Lock className="h-3.5 w-3.5" />
                      Locked in place for auto-schedule
                    </div>
                    {currentCall.scheduleLockedReason && (
                      <p className="mt-1 text-amber-800">{currentCall.scheduleLockedReason}</p>
                    )}
                    {currentCall.scheduleLockedAt && (
                      <p className="mt-0.5 text-2xs text-amber-700">
                        Locked {formatDate(currentCall.scheduleLockedAt)}
                      </p>
                    )}
                  </div>
                )}
                {currentCall.technician?.id && (
                  <button
                    type="button"
                    onClick={() => openMessageThread({
                      technicianId: currentCall.technician.id,
                      technician: currentCall.technician,
                      serviceCallId: currentCall.id,
                      serviceCallLabel: getServiceCallListCustomerName(currentCall),
                    })}
                    className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-surface-border px-2.5 py-1.5 text-xs font-medium text-primary-700 hover:bg-primary-50"
                  >
                    <MessageSquare className="h-3.5 w-3.5" />
                    Message {technicianName(currentCall.technician)}
                  </button>
                )}
              </div>
            </Section>
          )}

          {/* Contact */}
          {client && (
            <Section icon={User} title="Contact">
              <div className="space-y-1.5">
                <InfoRow
                  label="Name"
                  value={[client.contactFirstName, client.contactLastName].filter(Boolean).join(' ')}
                />
                {client.phone && (
                  <div className="flex items-center gap-2 text-xs">
                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                    <a href={`tel:${client.phone}`} className="text-primary-600 hover:text-primary-800">
                      {client.phone}
                    </a>
                  </div>
                )}
                {client.secondPhone && (
                  <div className="flex items-center gap-2 text-xs">
                    <Phone className="h-3.5 w-3.5 text-gray-400" />
                    <a href={`tel:${client.secondPhone}`} className="text-primary-600 hover:text-primary-800">
                      {client.secondPhone}
                    </a>
                    <span className="text-2xs text-gray-400">(alt)</span>
                  </div>
                )}
                {client.email && (
                  <div className="flex items-center gap-2 text-xs min-w-0">
                    <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                    <a
                      href={`mailto:${client.email}`}
                      className="text-primary-600 hover:text-primary-800 truncate"
                    >
                      {client.email}
                    </a>
                  </div>
                )}
                {fullAddress && (
                  <div className="flex items-start gap-2 text-xs">
                    <MapPin className="h-3.5 w-3.5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{fullAddress}</span>
                  </div>
                )}
              </div>
            </Section>
          )}

          {/* Flags */}
          {flags.length > 0 && (
            <Section title="Flags">
              <div className="flex flex-wrap gap-1.5">
                {flags.map((flag) => (
                  <span
                    key={flag.id}
                    className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-2xs text-blue-700"
                  >
                    <FlagIcon iconName={flag.icon} className="h-3 w-3" title={flag.name} />
                    {flag.name}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Access */}
          {client && (client.gateCode || client.omniMatrix) && (
            <Section icon={KeyRound} title="Access">
              <div className="space-y-1.5 rounded-md bg-amber-50 border border-amber-200 p-2">
                {client.gateCode && (
                  <InfoRow label="Gate code" value={client.gateCodeValue || 'Required'} />
                )}
                {client.omniMatrix && (
                  <InfoRow label="Omni Matrix" value={client.omniMatrixId || 'Yes'} />
                )}
              </div>
            </Section>
          )}

          {/* Generator */}
          {client && (client.generatorType || client.generatorModel || client.generatorSerialNumber) && (
            <Section icon={Wrench} title="Generator">
              <div className="space-y-1.5 rounded-md bg-blue-50 border border-blue-200 p-2">
                <InfoRow label="Type" value={client.generatorType} />
                <InfoRow label="Model" value={client.generatorModel} />
                <InfoRow label="Size" value={client.generatorSize} />
                <InfoRow label="Cooling" value={formatCoolingType(client.generatorCoolingType)} />
                <InfoRow label="Serial" value={client.generatorSerialNumber} />
                <InfoRow label="Exercise" value={client.generatorExerciseTime} />
              </div>
            </Section>
          )}

          {/* Filters & fluids */}
          {client && (client.airFilter || client.oilFilter1 || client.fuelFilter1 || client.oilType) && (
            <details className="group">
              <summary className="flex items-center gap-1 cursor-pointer text-2xs font-semibold uppercase tracking-wide text-gray-500 list-none">
                <ChevronRight className="h-3.5 w-3.5 transition-transform group-open:rotate-90" />
                Filters &amp; fluids
              </summary>
              <div className="mt-1.5 space-y-1 rounded-md bg-surface-secondary p-2">
                <InfoRow label="Air filter" value={client.airFilter} />
                <InfoRow label="Fuel filter 1" value={client.fuelFilter1} />
                <InfoRow label="Fuel filter 2" value={client.fuelFilter2} />
                <InfoRow label="Oil filter 1" value={client.oilFilter1} />
                <InfoRow label="Oil filter 2" value={client.oilFilter2} />
                <InfoRow label="Fuel additive" value={client.fuelAdditive} />
                <InfoRow label="Fuel type" value={client.fuelType} />
                <InfoRow label="Oil type" value={client.oilType} />
                <InfoRow label="Oil amount" value={client.oilAmount} />
                <InfoRow label="Battery size" value={client.batterySize} />
              </div>
            </details>
          )}

          {/* Contract */}
          {client?.isContractClient && (
            <Section icon={Calendar} title="Contract">
              <div className="space-y-1.5">
                <InfoRow
                  label="Last service"
                  value={client.lastContractServiceDate && formatDate(client.lastContractServiceDate)}
                />
                <InfoRow
                  label="Next service"
                  value={client.nextContractServiceDate && formatDate(client.nextContractServiceDate)}
                />
              </div>
            </Section>
          )}

          {/* Client notes */}
          {client?.notes && (
            <Section icon={FileText} title="Client notes">
              <p className="text-xs text-gray-700 whitespace-pre-wrap rounded-md bg-surface-secondary p-2">
                {client.notes}
              </p>
            </Section>
          )}

          {/* Past service calls */}
          {clientId && (
            <Section icon={Clock} title={`Service history${history.length ? ` (${history.length})` : ''}`}>
              {historyLoading || clientLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-500 py-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                </div>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-400">No past service calls.</p>
              ) : (
                <div className="space-y-1">
                  {history.map((call) => {
                    const date = call.completedDate || call.scheduledDate || call.createdAt;
                    return (
                      <button
                        key={call.id}
                        type="button"
                        onClick={() => openCall(call.id)}
                        disabled={loadingCallId === call.id}
                        className="w-full flex items-center gap-2 rounded-md border border-surface-border px-2 py-1.5 text-left hover:bg-surface-hover disabled:opacity-60"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-gray-900">
                              {date ? formatDate(date) : 'No date'}
                            </span>
                            <StatusPill status={call.status} />
                          </div>
                          {(call.type?.name || call.invoiceNumber) && (
                            <div className="text-2xs text-gray-500 truncate">
                              {call.type?.name}
                              {call.type?.name && call.invoiceNumber ? ' · ' : ''}
                              {call.invoiceNumber ? `Inv #${call.invoiceNumber}` : ''}
                            </div>
                          )}
                        </div>
                        {loadingCallId === call.id
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 flex-shrink-0" />
                          : <ChevronRight className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>
              )}
            </Section>
          )}

          {/* Map point (no client resolved) */}
          {point && !client && (
            <Section icon={MapPin} title="Location">
              <div className="text-xs capitalize text-gray-600">{point.entityType}</div>
              {point.subLabel && <div className="text-xs text-gray-500">{point.subLabel}</div>}
              {point.technicianName && (
                <div className="text-xs">Tech: {point.technicianName}</div>
              )}
            </Section>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex-shrink-0 p-3 border-t border-surface-border space-y-2">
          {item?.subgroup === 'repair_queue' && !currentCall?.scheduledDate && serviceCallId && (
            <button
              type="button"
              className="btn-primary w-full btn-sm inline-flex items-center justify-center gap-1.5"
              onClick={() => setScheduleRepairOpen(true)}
            >
              <CalendarPlus className="h-3.5 w-3.5" />
              Schedule…
            </button>
          )}
          {assignment?.client && (
            <button
              type="button"
              className="btn-secondary w-full btn-sm"
              onClick={() => {
                if (window.confirm('Return this assignment to the backlog?')) {
                  unassignAssignment.mutate(assignment.id);
                  setSelectedDetail(null);
                }
              }}
            >
              Unassign
            </button>
          )}
          {currentCall?.scheduledDate && (
            <button
              type="button"
              className="btn-secondary w-full btn-sm inline-flex items-center justify-center gap-1.5"
              disabled={scheduleLockMutation.isLoading}
              onClick={handleToggleScheduleLock}
            >
              {scheduleLockMutation.isLoading ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : currentCall.scheduleLocked ? (
                <Unlock className="h-3.5 w-3.5" />
              ) : (
                <Lock className="h-3.5 w-3.5" />
              )}
              {currentCall.scheduleLocked ? 'Unlock schedule' : 'Lock schedule'}
            </button>
          )}
          {currentCall?.scheduledDate && (
            <button
              type="button"
              className="btn-secondary w-full btn-sm"
              onClick={() => {
                unscheduleCall.mutate(currentCall.id);
                setSelectedDetail(null);
              }}
            >
              Unschedule
            </button>
          )}
        </div>
      </div>

      <ServiceCallDetailsModal
        serviceCall={openServiceCall}
        isOpen={!!openServiceCall}
        onClose={() => setOpenServiceCall(null)}
      />
      <ScheduleRepairModal
        serviceCallId={serviceCallId}
        isOpen={scheduleRepairOpen}
        onClose={() => {
          setScheduleRepairOpen(false);
          setSelectedDetail(null);
        }}
      />
    </Dock>
  );
}
